import React, { useEffect, useState } from 'react';
import Navbar from '../../../components/Navbar';
import { motion } from 'framer-motion';
import { 
  Crown, CheckCircle2, XCircle, Zap, Users, BarChart3, MessageSquare,
  History, TrendingUp, Shield, AlertTriangle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import VendorRequests from '../../../requests/vendor/VendorRequests';
import { toast } from 'react-toastify';
import Loader from '../../../components/Loader';
import { usePermissionCheck } from '../../../hooks/permissionHooks/usePermissionCheck';
import { PERMISSIONS } from '../../../config/permissions';
import PermissionAwareButton from '../../../components/Permissions/PermissionAwareButton';
import SubscriptionPermissionTest from '../../../components/Permissions/SubscriptionPermissionTest';

const Subscription = () => {
  const user = useSelector((state) => state?.user?.user) || null;
  const subscription_details = user?.subscription || null;
  const plan_features = subscription_details?.subscription_plan?.features || null;
  const [isLoading, setIsLoading] = useState(false);
  const [planDetails, setPlanDetails] = useState(null);
  const [totalDeals, setTotalDeals] = useState(0);
  const [totalLeads, setTotalLeads] = useState(0);
  const [totalSenderIds, setTotalSenderIds] = useState(0);
  const [totalPromotions, setTotalPromotions] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [showPermissionTest, setShowPermissionTest] = useState(false);
  
  const navigate = useNavigate();

  // Permission checks
  const { 
    canPerform, 
    isAuthenticated,
    isMainVendor,
    userPermissions 
  } = usePermissionCheck();

  // Check specific permissions
  const canViewSubscription = canPerform(PERMISSIONS.MODULES.SUBSCRIPTION, PERMISSIONS.ACTIONS.VIEW_SUBSCRIPTION) || isMainVendor;
  const canPurchaseSubscription = canPerform(PERMISSIONS.MODULES.SUBSCRIPTION, PERMISSIONS.ACTIONS.PURCHASE_SUBSCRIPTION) || isMainVendor;
  const canViewSubscriptionHistory = canPerform(PERMISSIONS.MODULES.SUBSCRIPTION, PERMISSIONS.ACTIONS.VIEW_SUBSCRIPTION_HISTORY) || isMainVendor;

  // Temporary bypass for testing
  const tempCanViewSubscription = true;

  console.log('Subscription Permission Debug:', {
    isAuthenticated,
    isMainVendor,
    canViewSubscription,
    canPurchaseSubscription,
    canViewSubscriptionHistory,
    userPermissions,
    userRole: user?.role,
    user: user,
    PERMISSIONS_MODULE: PERMISSIONS.MODULES.SUBSCRIPTION,
    PERMISSIONS_ACTION: PERMISSIONS.ACTIONS.VIEW_SUBSCRIPTION,
    userObject: user,
    userPermissionsType: typeof userPermissions,
    userPermissionsIsArray: Array.isArray(userPermissions),
    tempCanViewSubscription
  });

  useEffect(()=>{
    const fetchVendorPlansDetails = async()=>{
      if (!tempCanViewSubscription) return; // Use temporary bypass
      
      try{
        setIsLoading(true)
        const response = await VendorRequests.getVendorPlansDetails();
        if(response?.data?.status_code === "000" && !response?.data?.in_error){
          setPlanDetails(response?.data?.data)
          setTotalDeals(response?.data?.data?.total_deals)
          setTotalLeads(response?.data?.data?.total_sms_leads)
          setTotalSenderIds(response?.data?.data?.total_sender_ids)
          setTotalPromotions(response?.data?.data?.total_promoted_deals)
          setTotalUsers(response?.data?.data?.total_users)
          setIsLoading(false);
        }
        else{
          toast.error("Sorry, an error occurred while fetching your plans details")
        }
      }catch(error){
        toast.error("Sorry, an error occurred while fetching your plans details")
      }finally{
        setIsLoading(false)
      }
    }
    fetchVendorPlansDetails();
  },[tempCanViewSubscription])

  // Helper function to calculate days remaining
  const calculateDaysRemaining = () => {
    if (!subscription_details?.end_date) return 0;
    const endDate = new Date(subscription_details.end_date);
    const today = new Date();
    const diffTime = endDate - today;
    const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, daysRemaining);
  };

  // Status banner section
  const getSubscriptionStatus = () => {
    if (!subscription_details) return false;
    if (calculateDaysRemaining() === 0) return false;
    return subscription_details.status === 'active';
  };

  const getStatusBannerStyle = () => {
    if (calculateDaysRemaining() === 0) {
      return 'bg-gradient-to-r from-red-500 to-red-600';
    }

    const planName = subscription_details?.subscription_plan?.name?.toLowerCase() || '';
    
    switch(planName) {
      case 'free trial':
        return 'bg-gradient-to-r from-amber-400 to-orange-600';
      case 'pro plan':
        return 'bg-gradient-to-r from-violet-500 to-purple-600';
      case 'basic plan':
        return 'bg-gradient-to-r from-blue-400 to-indigo-600';
      case 'enterprise plan':
        return 'bg-gradient-to-r from-emerald-400 to-teal-600';
      default:
        return 'bg-gradient-to-r from-gray-400 to-gray-600';
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const handleButtonClick = ()=>{
    navigate("/pricing")
  }

  const renderUsageBar = (used, total, label, icon) => (
    <div className="p-4 bg-white rounded-xl border border-gray-100 transition-all duration-200 hover:border-indigo-100 hover:shadow-sm">
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center space-x-3">
          <div className="p-2 text-indigo-600 bg-indigo-50 rounded-lg">
            {icon}
          </div>
          <span className="text-sm font-medium text-gray-700">{label}</span>
        </div>
        <span className="text-sm font-medium text-gray-500">{used}/{total}</span>
      </div>
      <div className="overflow-hidden w-full h-2 bg-gray-100 rounded-full">
        <div 
          className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-300"
          style={{ width: `${Math.min((used/total) * 100, 100)}%` }}
        />
      </div>
    </div>
  );

  if (!isAuthenticated || !tempCanViewSubscription) { // Use temporary bypass
    return (
      <div className="bg-white">
        <Navbar page="Subscription" />
        
        {/* Debug Section */}
        <div className="p-4 bg-yellow-50 border-b border-yellow-200">
          <h3 className="mb-2 font-bold text-yellow-800">Debug Information:</h3>
          <div className="space-y-1 text-sm text-yellow-700">
            <p><strong>isAuthenticated:</strong> {isAuthenticated ? 'Yes' : 'No'}</p>
            <p><strong>isMainVendor:</strong> {isMainVendor ? 'Yes' : 'No'}</p>
            <p><strong>canViewSubscription:</strong> {canViewSubscription ? 'Yes' : 'No'}</p>
            <p><strong>tempCanViewSubscription:</strong> {tempCanViewSubscription ? 'Yes' : 'No'}</p>
            <p><strong>userPermissions:</strong> {JSON.stringify(userPermissions)}</p>
            <p><strong>Module:</strong> {PERMISSIONS.MODULES.SUBSCRIPTION}</p>
            <p><strong>Action:</strong> {PERMISSIONS.ACTIONS.VIEW_SUBSCRIPTION}</p>
          </div>
        </div>
        
        <div className="flex justify-center items-center min-h-[80vh] bg-gray-50">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-8 max-w-md text-center"
          >
            <div className="inline-block p-4 mb-6 bg-red-100 rounded-full">
              <Shield className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="mb-4 text-2xl font-bold text-gray-800">Access Restricted</h2>
            <p className="mb-6 leading-relaxed text-gray-600">
              You don't have permission to view subscription details. Please contact your administrator to request access to the Subscription Management module.
            </p>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex gap-2 items-center mb-2">
                <AlertTriangle className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">Required Permission:</span>
              </div>
              <p className="text-sm text-blue-700">View Subscription in Subscription Management</p>
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/dashboard')}
              className="mt-6 px-6 py-3 bg-[#FF6C6C] text-white rounded-lg hover:bg-[#FF6C6C]/90 transition-colors font-medium"
            >
              Return to Dashboard
            </motion.button>
          </motion.div>
        </div>
      </div>
    );
  }

  if(isLoading){
    return <Loader/>
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <Navbar page={"Subscription"} />
      <div className="px-4 py-16 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="mx-auto max-w-7xl"
        >
          {/* Permission Test Button - Temporary for debugging */}
          {/* <div className="mb-6 text-right">
            <button
              onClick={() => setShowPermissionTest(!showPermissionTest)}
              className="px-3 py-2 text-sm text-white bg-blue-500 rounded-lg transition-colors hover:bg-blue-600"
            >
              {showPermissionTest ? 'Hide' : 'Show'} Permission Test
            </button>
          </div> */}

          {/* Permission Test Section */}
          {showPermissionTest && (
            <div className="mb-8">
              <SubscriptionPermissionTest />
            </div>
          )}

          {/* Main Card */}
          <motion.div
            variants={itemVariants}
            className="overflow-hidden bg-white rounded-2xl shadow-xl"
          >
            {/* Status Banner */}
            <div className={`px-6 py-4 ${getStatusBannerStyle()}`}>
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  {getSubscriptionStatus() ? (
                    <CheckCircle2 className="text-white" size={24} />
                  ) : (
                    <XCircle className="text-white" size={24} />
                  )}
                  <h2 className="text-xl font-semibold text-white">
                    {subscription_details?.subscription_plan?.name || 'No Plan'}
                  </h2>
                </div>
                <div className="flex items-center px-4 py-1 bg-white bg-opacity-20 rounded-full">
                  <Crown className="mr-2 text-white" size={20} />
                  <span className="font-medium text-white">
                    {calculateDaysRemaining() === 0 ? (
                      'Expired'
                    ) : (
                      `${calculateDaysRemaining()} days remaining`
                    )}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-8">
              {/* Plan Info & Billing Section */}
              <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-3">
                <div className="p-5 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-100">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-sm font-medium text-gray-600">Current Plan</span>
                    <span className="px-3 py-1 text-xs font-medium text-indigo-700 bg-white rounded-full border border-indigo-100 shadow-sm">
                      {subscription_details?.subscription_plan?.name || 'No Plan'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-2">
                      <Crown className="text-indigo-600" size={20} />
                      <span className="text-2xl font-bold text-gray-900">
                        ₵{subscription_details?.subscription_plan?.price || 0}
                      </span>
                    </div>
                    <span className="text-sm text-gray-500">
                      {subscription_details?.subscription_plan?.duration_days || 30} days
                    </span>
                  </div>
                </div>

                <div className="p-5 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                  <div className="flex items-center mb-4 space-x-2">
                    <History size={16} className="text-purple-600" />
                    <span className="text-sm font-medium text-gray-600">Next Payment</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Payment Date</span>
                      <span className="text-sm font-medium text-gray-900">
                        {subscription_details?.end_date 
                          ? new Date(subscription_details.end_date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })
                          : 'Not Available'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-5 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-100">
                  <div className="flex items-center mb-4 space-x-2">
                    <TrendingUp size={16} className="text-emerald-600" />
                    <span className="text-sm font-medium text-gray-600">Total Allowed Leads</span>
                  </div>
                  <div className="mb-1 text-2xl font-bold text-gray-900">
                    {totalLeads}
                  </div>
                  <span className="text-sm text-gray-600">Total Reach for Campaigns</span>
                </div>
              </div>

              {/* Usage Overview Section */}
              <div className="space-y-6">
                <h3 className="flex items-center space-x-2 text-lg font-semibold text-gray-900">
                  <TrendingUp className="text-indigo-600" size={20} />
                  <span>Usage Overview</span>
                </h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {renderUsageBar(
                    parseInt(totalDeals), 
                    plan_features?.max_deals || plan_features?.max_offers || parseInt(totalDeals),
                    'Deals',
                    <Users size={18} />
                  )}
                  {renderUsageBar(parseInt(totalSenderIds), plan_features?.max_sender_ids || parseInt(totalSenderIds), 'Sender IDs',
                    <BarChart3 size={18} />
                  )}
                  {renderUsageBar(parseInt(totalPromotions), plan_features?.max_promotions || parseInt(totalPromotions), 'Promoted Deals',
                    <Zap size={18} />
                  )}
                  {renderUsageBar(parseInt(totalUsers), plan_features?.max_users || plan_features?.number_of_users || parseInt(totalUsers), 'Added Users',
                    <Users size={18} />
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 justify-center mt-8">
                {/* Permission-aware Upgrade Button */}
                <PermissionAwareButton
                  permission={{
                    module: PERMISSIONS.MODULES.SUBSCRIPTION,
                    action: PERMISSIONS.ACTIONS.PURCHASE_SUBSCRIPTION
                  }}
                  fallbackForMainVendor={true}
                  onClick={handleButtonClick}
                  className="px-8 py-3 bg-gradient-to-r from-[#FF6C6C] to-[#ff8282] hover:from-[#ff5c5c] hover:to-[#ff7272] text-white font-medium rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 shadow-sm hover:shadow-md"
                >
                  <Zap size={16} />
                  <span>Upgrade Subscription</span>
                </PermissionAwareButton>

                {/* Permission-aware Payment History Button */}
                <PermissionAwareButton
                  permission={{
                    module: PERMISSIONS.MODULES.SUBSCRIPTION,
                    action: PERMISSIONS.ACTIONS.VIEW_SUBSCRIPTION_HISTORY
                  }}
                  fallbackForMainVendor={true}
                  onClick={() => navigate('/payment-history')}
                  className="flex justify-center items-center px-8 py-3 space-x-2 font-medium text-gray-700 bg-white rounded-lg border border-gray-200 shadow-sm transition-all duration-300 group hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 hover:text-indigo-600 hover:border-indigo-200 hover:shadow-indigo-100"
                >
                  <History size={16} className="transition-transform duration-300 transform group-hover:rotate-12" />
                  <span>Payment History</span>
                </PermissionAwareButton>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default Subscription;